import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, ClockIcon, FilePlus2Icon, PackagePlusIcon, PlusIcon } from 'lucide-react';
import { useAdminData } from '../../contexts/AdminDataContext';
import { useTimer } from '../../contexts/TimerContext';
import { ActiveTimerCard } from '../../components/admin/today/ActiveTimerCard';
import { ActiveCaseCard } from '../../components/admin/today/ActiveCaseCard';
import { CasePickerDialog } from '../../components/admin/today/CasePickerDialog';
import { EntryDialog } from '../../components/admin/case/EntryDialog';
import type { EntryDialogTarget } from '../../components/admin/case/EntryDialog';
import type { Case } from '../../types/admin';
import { todayLong } from '../../utils/adminDates';
import { attentionItems, isActiveCase, sortActiveCases } from '../../utils/todayAttention';
type PickFor = 'hours' | 'material' | null;
const toneDot: Record<'normal' | 'warn' | 'danger', string> = {
  normal: 'bg-status-info',
  warn: 'bg-status-warn',
  danger: 'bg-danger'
};

/** Startside på mobil og i den installerede app: dagens arbejde på ét sted. */
export function Today() {
  const {
    cases,
    enquiries,
    quotes,
    invoices,
    dataLoading
  } = useAdminData();
  const {
    timer
  } = useTimer();
  const navigate = useNavigate();
  const [pickFor, setPickFor] = useState<PickFor>(null);
  const [dialog, setDialog] = useState<EntryDialogTarget | null>(null);
  const active = useMemo(() => sortActiveCases(cases.filter(isActiveCase)), [cases]);
  const attention = useMemo(() => attentionItems({
    cases,
    enquiries,
    quotes,
    invoices,
    runningCaseId: timer?.caseId
  }), [cases, enquiries, quotes, invoices, timer?.caseId]);
  function pick(item: Case) {
    const kind = pickFor === 'material' ? 'material' : 'hours';
    setPickFor(null);
    setDialog({
      caseId: item.id,
      kind
    });
  }
  const quickActions = [{
    label: 'Registrér timer',
    icon: ClockIcon,
    onClick: () => setPickFor('hours')
  }, {
    label: 'Tilføj materiale',
    icon: PackagePlusIcon,
    onClick: () => setPickFor('material')
  }, {
    label: 'Opret sag',
    icon: PlusIcon,
    onClick: () => navigate('/admin/sager/ny')
  }, {
    label: 'Opret tilbud',
    icon: FilePlus2Icon,
    onClick: () => navigate('/admin/tilbud/ny')
  }];
  return <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10" data-id="element-1842" data-mp="2w0m8mn3y4yxc">
      <h1 className="font-display text-[clamp(1.9rem,6vw,2.5rem)] font-bold leading-none tracking-tight text-ink" data-id="element-1843" data-mp="2vztaubmytjrc">I dag</h1>
      <p className="mt-2 text-[15px] text-ink-muted" data-id="element-1844" data-mp="23bx6ckr5mnk0">{todayLong()}</p>

      <div className="mt-6 space-y-6" data-id="element-1845" data-mp="23c6zgh7hxatx">
        <ActiveTimerCard data-id="element-1846" data-mp="28ba3bybqpb0b" />

        <div className="grid grid-cols-2 gap-2 md:grid-cols-4" data-id="element-1847" data-mp="289z142xy0bbb">
          {quickActions.map(action => <button key={action.label} type="button" onClick={action.onClick} className="flex h-14 items-center gap-2.5 rounded-xl border border-line bg-white px-3.5 text-left text-sm font-medium text-ink transition-colors duration-150 hover:border-accent/50" data-id="element-1848" data-mp="2d2ifv38d9chp">
              <action.icon className="h-5 w-5 shrink-0 text-accent" aria-hidden="true" data-id="element-1849" data-mp="1czu29j8gdti6" />
              <span className="truncate" data-id="element-1850" data-mp="183mtzjsl45x2">{action.label}</span>
            </button>)}
        </div>

        <section aria-labelledby="aktive-sager" data-id="element-1851" data-mp="135tpqwaudlzf">
          <div className="flex items-baseline justify-between gap-3" data-id="element-1852" data-mp="z2x1aoymevnk">
            <h2 id="aktive-sager" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1853" data-mp="u32cqs2bii5o">
              Aktive sager
            </h2>
            <Link to="/admin/sager" className="whitespace-nowrap text-sm text-ink-muted hover:text-ink" data-id="element-1854" data-mp="pbkck684cllr">
              Alle sager
            </Link>
          </div>
          {dataLoading ? <p className="mt-3 text-[15px] text-ink-muted" data-id="element-1855" data-mp="ff7lzh7venv8">Henter sager …</p> : active.length === 0 ? <p className="mt-3 text-[15px] text-ink-muted" data-id="element-1856" data-mp="bc1hae0ep4qv">Ingen sager er planlagt eller i gang lige nu.</p> : <ul className="mt-3 space-y-3" data-id="element-1857" data-mp="6fkpn9u00891">
              {active.map(item => <ActiveCaseCard key={item.id} caseItem={item} onRegisterHours={caseItem => setDialog({
            caseId: caseItem.id,
            kind: 'hours'
          })} data-id="element-1858" data-mp="26gixt371td3i" />)}
            </ul>}
        </section>

        <section aria-labelledby="opmaerksomhed" data-id="element-1859" data-mp="21npumjr8actj">
          <h2 id="opmaerksomhed" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1860" data-mp="1mrqf4o1shaed">
            Kræver opmærksomhed
          </h2>
          {attention.length === 0 ? <p className="mt-3 text-[15px] text-ink-muted" data-id="element-1861" data-mp="1rogtn6xy3xxo">Intet kræver din opmærksomhed lige nu.</p> : <ul className="mt-2 divide-y divide-line" data-id="element-1862" data-mp="1ipvcng84gqz0">
              {attention.map(item => <li key={item.id} data-id="element-1863" data-mp="385codxdcdw55">
                  <Link to={item.to} className="flex min-h-[3rem] items-center gap-3 py-2 text-[15px] text-ink" data-id="element-1864" data-mp="3864hn1jgvhs9">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${toneDot[item.tone]}`} aria-hidden="true" data-id="element-1865" data-mp="3d3nz0abgoaof" />
                    <span className="min-w-0 flex-1 truncate" data-id="element-1866" data-mp="3n8cswywsv350">{item.text}</span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-1867" data-mp="3iaba8f3ad5u2" />
                  </Link>
                </li>)}
            </ul>}
        </section>
      </div>

      <CasePickerDialog open={pickFor !== null} title={pickFor === 'material' ? 'Tilføj materiale' : 'Registrér timer'} cases={active} onPick={pick} onClose={() => setPickFor(null)} data-id="element-1868" data-mp="3r8epwurlb8dy" />
      <EntryDialog target={dialog} onClose={() => setDialog(null)} data-id="element-1869" data-mp="itmocvheb3jw" />
    </div>;
}